const hasAllInboxesAccess = (role) =>
  role.permissions.some((perm) => perm.name === 'ACCESS_ALL_INBOXES');

// The inboxes a role grants, out of `inboxes`: all of them for a role with
// ACCESS_ALL_INBOXES, otherwise its targeted ACCESS_INBOX inboxes.
export const getRoleInboxes = (role, inboxes) => {
  if (hasAllInboxesAccess(role)) return inboxes;

  const targetedIds = role.permissions
    .filter((tp) => tp.name === 'ACCESS_INBOX' && tp.targetType === 'Inbox')
    .map((tp) => tp.targetId);
  return inboxes.filter((inbox) => targetedIds.includes(inbox.id));
};

// Function to organize inboxes by roles
export const organizeInboxesByRoles = (userPermissions, filteredInboxes) => {
  if (!userPermissions) return { roles: [], individualInboxes: filteredInboxes };

  const roles = [];
  const inboxesByRole = {};
  const individualInboxes = [...filteredInboxes];

  userPermissions.roles.forEach((role) => {
    const roleInboxes = getRoleInboxes(role, filteredInboxes);
    if (roleInboxes.length === 0 && !hasAllInboxesAccess(role)) return;

    roles.push({ id: role.id, name: role.name, inboxes: roleInboxes });
    inboxesByRole[role.id] = roleInboxes;
  });

  return { roles, inboxesByRole, individualInboxes };
};

// Function to filter inboxes based on permissions
export const filterInboxesByPermissions = (allInboxes, permissions, userId) => {
  // Check if user has ACCESS_ALL_INBOXES permission
  const hasAccessAllInboxes = permissions.roles.some((role) =>
    role.permissions.some((perm) => perm.name === "ACCESS_ALL_INBOXES")
  );

  if (hasAccessAllInboxes) {
    return allInboxes; // User can access all inboxes
  }

  return allInboxes.filter((inbox) => {
    // Check if inbox is associated with this user
    if (inbox.userId === userId) {
      return true;
    }

    // Check if user has targeted permission for this inbox
    return permissions.roles.some((role) =>
      role.permissions.some(
        (tp) =>
          tp.name === "ACCESS_INBOX" &&
          tp.targetType === "Inbox" &&
          tp.targetId === inbox.id
      )
    );
  });
};

// Display label for an inbox: "Owner Name (contact)" when the owner is known,
// otherwise just the contact. Handles the differing inbox shapes across the app
// (user.name, user.fullDisplayName, or ownerName).
export const formatInboxLabel = (inbox) => {
  if (!inbox) return 'Unknown';
  const contact =
    inbox.phoneNumber ||
    inbox.email ||
    (inbox.id ? `Inbox ${inbox.id}` : 'No contact info');
  const ownerName =
    inbox.user?.name || inbox.user?.fullDisplayName || inbox.ownerName || null;
  return ownerName ? `${ownerName} (${contact})` : contact;
};
